/**
 * Header Component
 *
 * Professional header with NO Web3 branding
 * Clean, trustworthy, legal-focused navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const profileMenuRef = useRef(null);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-content">
          {/* Logo and Brand */}
          <Link to="/" className="brand">
            <div className="brand-icon">⚖</div>
            <div className="brand-text">
              <span className="brand-name">Evidencer</span>
              <span className="brand-tagline">Evidence Infrastructure</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav-desktop">
            <Link
              to="/"
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              Home
            </Link>
            <Link
              to="/evidence"
              className={`nav-link ${isActive('/evidence') ? 'active' : ''}`}
            >
              Upload Evidence
            </Link>
            <Link
              to="/use-cases"
              className={`nav-link ${isActive('/use-cases') ? 'active' : ''}`}
            >
              Use Cases
            </Link>
            <Link
              to="/verify"
              className={`nav-link ${isActive('/verify') ? 'active' : ''}`}
            >
              Verify
            </Link>
            <Link
              to="/enterprise"
              className={`nav-link nav-link-cta ${isActive('/enterprise') ? 'active' : ''}`}
            >
              Enterprise
            </Link>

            {/* Authentication Button */}
            {isAuthenticated ? (
              <div className="profile-menu-container" ref={profileMenuRef}>
                <button
                  className="profile-button"
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  aria-label="User menu"
                >
                  <span className="profile-icon">👤</span>
                  <span className="profile-name">{user?.username || user?.firstName || 'User'}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M6 9L1 4h10L6 9z" />
                  </svg>
                </button>
                {profileMenuOpen && (
                  <div className="profile-dropdown">
                    <div className="profile-dropdown-header">
                      <div className="profile-dropdown-name">
                        {user?.firstName && user?.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user?.username}
                      </div>
                      {user?.email && (
                        <div className="profile-dropdown-email">{user.email}</div>
                      )}
                      {user?.userRole && (
                        <div className="profile-dropdown-role">{user.userRole}</div>
                      )}
                    </div>
                    <div className="profile-dropdown-divider"></div>
                    <button
                      onClick={handleLogout}
                      className="profile-dropdown-item logout-button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="nav-link nav-link-login">
                Sign In
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="nav-mobile">
            <Link
              to="/"
              className={`nav-link-mobile ${isActive('/') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/evidence"
              className={`nav-link-mobile ${isActive('/evidence') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Upload Evidence
            </Link>
            <Link
              to="/use-cases"
              className={`nav-link-mobile ${isActive('/use-cases') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Use Cases
            </Link>
            <Link
              to="/verify"
              className={`nav-link-mobile ${isActive('/verify') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Verify
            </Link>
            <Link
              to="/enterprise"
              className={`nav-link-mobile nav-link-cta ${isActive('/enterprise') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Enterprise Solutions
            </Link>

            {/* Mobile Authentication */}
            {isAuthenticated ? (
              <>
                <div className="mobile-profile-info">
                  <div className="mobile-profile-icon">👤</div>
                  <div>
                    <div className="mobile-profile-name">
                      {user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.username}
                    </div>
                    {user?.email && (
                      <div className="mobile-profile-email">{user.email}</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="nav-link-mobile logout-button-mobile"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="nav-link-mobile nav-link-login"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;
